/**
 * Typed calls for the `/challenges*` endpoints.
 *
 * `src/api/schema.ts` is generated from the *deployed* ACC OpenAPI doc
 * (`generate:api` in package.json), which predates `/challenges` entirely —
 * this WP's API changes (`../nolatr-team-dashboard-api-php`, branch
 * `fase2/wp-05`) live only in that repo's own worktree and are not deployed
 * there yet. `src/features/statistics/api.ts` already hit this: declaration-
 * merging new paths into the generated `paths` interface tips `tsc` into
 * "Expression produces a union type that is too complex to represent"
 * (`SuccessJson` in `client.ts`, evaluated over `keyof paths`) once the
 * generated file is large enough — so, same as that file, these are
 * dedicated functions built on `client.ts`'s primitives (`buildUrl`,
 * `readReason`, `errorForStatus`) rather than its generic `paths`-driven
 * `apiGet`/`apiPost`/etc. Once a real `generate:api` run includes
 * `/challenges`, call sites can switch back — the domain types below stay
 * either way.
 *
 * Shapes mirror the API's own source: `php/api_challenges.php`.
 */

import { buildUrl, readReason } from '@/api/client';
import { errorForStatus, NetworkError } from '@/api/errors';
import { getAccessToken } from '@/features/auth/token';

export type ChallengeMode = 'individual' | 'group';
export type ChallengeStatus = 'draft' | 'active' | 'paused' | 'completed';

export interface Challenge {
  id: string;
  company_id: string;
  title: string;
  description: string | null;
  type: string;
  mode: ChallengeMode;
  start_date: string;
  end_date: string;
  goal_value: number;
  unit: string;
  reward: string | null;
  leaderboard_visible: boolean;
  status: ChallengeStatus;
  created_by: string;
  created_at: string;
  /** Only present on the GET /challenges list. */
  participant_count?: number;
  /**
   * Percentage of participants with `progress_value > 0` (`php/api_challenges.php`'s
   * `getAll()`). `null` when the challenge has zero participants — optional
   * only because older cached responses/tests may omit it; the card hides
   * the tile rather than showing an invented number when it's absent.
   */
  engagement_rate?: number | null;
  /** Percentage of participants with `progress_value >= goal_value`. Same
   * shape/rationale as `engagement_rate`. */
  completion_rate?: number | null;
}

export interface ChallengeCounts {
  active: number;
  draft: number;
  completed: number;
  total: number;
  total_participants: number;
}

export interface ChallengeList {
  counts: ChallengeCounts;
  challenges: Challenge[];
}

export interface ChallengeLeaderboardEntry {
  user_id: string;
  name: string | null;
  progress_value: number;
  joined_at: string;
  rank: number;
}

export interface ChallengeWritePayload {
  title: string;
  description?: string | null;
  type: string;
  mode: ChallengeMode;
  start_date: string;
  end_date: string;
  goal_value: number;
  unit: string;
  reward?: string | null;
  leaderboard_visible?: boolean;
}

/** Mirrors `client.ts`'s private `request()` — see the file header for why this isn't shared. */
async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  options: { params?: Record<string, string>; query?: Record<string, string>; body?: unknown } = {},
): Promise<T> {
  const url = buildUrl(path, options.params, options.query);

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : null,
    });
  } catch (cause) {
    throw new NetworkError(`Request to ${path} failed: ${String(cause)}`);
  }

  if (!response.ok) {
    throw errorForStatus(response.status, `${method} ${path} failed`, await readReason(response));
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (text === '') return undefined as T;
  return JSON.parse(text) as T;
}

export function listChallenges(
  companyId: string,
  status?: ChallengeStatus,
): Promise<ChallengeList> {
  return request('GET', '/challenges', {
    query: status !== undefined ? { company_id: companyId, status } : { company_id: companyId },
  });
}

export function getChallenge(challengeId: string): Promise<Challenge> {
  return request('GET', '/challenges/{challengeId}', { params: { challengeId } });
}

export function createChallenge(
  companyId: string,
  payload: ChallengeWritePayload,
): Promise<{ challenge_id: string }> {
  return request('POST', '/challenges', { body: { ...payload, company_id: companyId } });
}

export function updateChallenge(
  challengeId: string,
  payload: ChallengeWritePayload,
): Promise<{ challenge_id: string }> {
  return request('PUT', '/challenges/{challengeId}', {
    params: { challengeId },
    body: payload,
  });
}

export function setChallengeStatus(
  challengeId: string,
  status: ChallengeStatus,
): Promise<{ challenge_id: string; status: ChallengeStatus }> {
  return request('PUT', '/challenges/{challengeId}/status', {
    params: { challengeId },
    body: { status },
  });
}

export function joinChallenge(challengeId: string, userId?: string): Promise<void> {
  return request('POST', '/challenges/{challengeId}/participants', {
    params: { challengeId },
    body: userId !== undefined ? { user_id: userId } : {},
  });
}

export function getChallengeLeaderboard(challengeId: string): Promise<ChallengeLeaderboardEntry[]> {
  return request('GET', '/challenges/{challengeId}/leaderboard', { params: { challengeId } });
}
